/**
 * Compatibility Score — calcola match % tra le preferenze utente e un immobile.
 *
 * Segnali (peso ~equidistribuito):
 *  - city              → 30 punti se match (contains, case-insensitive)
 *  - max_price         → 30 punti se prop.price <= pref_max_price, decresce lineare fino a 0 se +30% oltre
 *  - min_rooms         → 20 punti se prop.rooms >= min, altrimenti scalare
 *  - min_sqm           → 20 punti se prop.surface_sqm >= min, altrimenti scalare
 *
 * Se una preferenza è mancante, il criterio è "neutro" (metà del peso).
 * Ritorna intero 0..100.
 */
export function computeMatchScore(prefs, property) {
    if (!prefs || !property) return null;
    const hasAny = ["pref_city", "pref_max_price", "pref_min_rooms", "pref_min_sqm"].some((k) => prefs[k]);
    if (!hasAny) return null;

    // City (30)
    let cityScore;
    if (prefs.pref_city) {
        const pc = String(prefs.pref_city).toLowerCase().trim();
        const pr = String(property.city || "").toLowerCase().trim();
        cityScore = pr && (pr.includes(pc) || pc.includes(pr)) ? 30 : 0;
    } else {
        cityScore = 15;
    }

    // Max price (30) — decresce linearmente da 100% (in budget) a 0% (30% sopra budget)
    let priceScore;
    if (prefs.pref_max_price && property.price) {
        const budget = Number(prefs.pref_max_price);
        const price = Number(property.price);
        if (price <= budget) priceScore = 30;
        else {
            const over = (price - budget) / budget;
            priceScore = Math.max(0, Math.round(30 * (1 - over / 0.3)));
        }
    } else priceScore = 15;

    // Min rooms (20)
    let roomsScore;
    if (prefs.pref_min_rooms && property.rooms != null) {
        const min = Number(prefs.pref_min_rooms);
        const r = Number(property.rooms);
        if (r >= min) roomsScore = 20;
        else roomsScore = Math.max(0, Math.round(20 * (r / min)));
    } else roomsScore = 10;

    // Min sqm (20)
    let sqmScore;
    if (prefs.pref_min_sqm && property.surface_sqm != null) {
        const min = Number(prefs.pref_min_sqm);
        const s = Number(property.surface_sqm);
        if (s >= min) sqmScore = 20;
        else sqmScore = Math.max(0, Math.round(20 * (s / min)));
    } else sqmScore = 10;

    return Math.round(cityScore + priceScore + roomsScore + sqmScore);
}

/**
 * Ritorna le preferenze correnti (loggato → user.preferences, guest → localStorage last search).
 */
export function readUserPrefs(user) {
    if (user?.preferences && Object.keys(user.preferences).length > 0) {
        return user.preferences;
    }
    try {
        const raw = localStorage.getItem("vd_last_search");
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

/**
 * Salva l'ultima ricerca nel localStorage (per anonymous match).
 * `filters` viene dal Listings state.
 */
export function persistLastSearch(filters) {
    if (!filters) return;
    const prefs = {};
    if (filters.city) prefs.pref_city = filters.city;
    if (filters.max_price) prefs.pref_max_price = Number(filters.max_price);
    if (filters.min_rooms) prefs.pref_min_rooms = Number(filters.min_rooms);
    if (filters.min_sqm) prefs.pref_min_sqm = Number(filters.min_sqm);
    if (Object.keys(prefs).length === 0) return;  // Non sovrascrivere prefs precedenti se il ricercatore ha un catalogo vuoto
    try {
        localStorage.setItem("vd_last_search", JSON.stringify(prefs));
    } catch { /* silent */ }
}
